import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import type { AutoCatalog, AutoImportRow } from "../types";
import {
  ALL, PRESET_HOURS, PeriodSel, durationText, fileSel, fmtDT, isoToSec, presetSel, resolve,
  secToDate, secToIso, secToTime, stepFor, stepText,
} from "../utils/period";

// Bottom sheet for picking the telemetry period: quick presets, uploaded
// files, or a custom range with date + time to the second. Applies with the
// «Применить» button; the sheet itself keeps a draft until then.
export default function PeriodPicker({ label, unit = 0, catalog, value, onApply, onClose, showFiles = true, showStep = true }: {
  label: string; unit?: number; catalog: AutoCatalog | null; value: PeriodSel;
  onApply: (sel: PeriodSel) => void; onClose: () => void; showFiles?: boolean; showStep?: boolean;
}) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [files, setFiles] = useState<AutoImportRow[]>([]);
  const [draft, setDraft] = useState<PeriodSel>(value);
  // Custom fields (strings, as the inputs hold them).
  const init = resolve(value, catalog?.data_from, catalog?.data_to);
  const [fD, setFD] = useState(init ? secToDate(init.from) : "");
  const [fT, setFT] = useState(init ? secToTime(init.from) : "00:00:00");
  const [tD, setTD] = useState(init ? secToDate(init.to) : "");
  const [tT, setTT] = useState(init ? secToTime(init.to) : "23:59:59");

  useEffect(() => {
    if (!showFiles) return;
    api.get<AutoImportRow[]>("/api/scada_v2/imports/recent").then(rows => setFiles(rows.filter(r => r.unit === unit))).catch(() => undefined);
  }, [unit, showFiles]);

  const custom = useMemo(() => {
    if (!fD || !tD) return null;
    const a = isoToSec(`${fD}T${fT || "00:00:00"}`), b = isoToSec(`${tD}T${tT || "23:59:59"}`);
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    return { from: a, to: b };
  }, [fD, fT, tD, tT]);

  // Any chip → its selection + mirror it into the custom fields, so «±» and
  // manual edits start from what the chip chose.
  const pick = (sel: PeriodSel) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setDraft(sel);
    const r = resolve(sel, catalog?.data_from, catalog?.data_to);
    if (r) { setFD(secToDate(r.from)); setFT(secToTime(r.from)); setTD(secToDate(r.to)); setTT(secToTime(r.to)); }
  };
  const editCustom = (setter: (v: string) => void) => (v: string) => { setter(v); setDraft(d => ({ ...d, kind: "custom", preset: undefined, file: undefined })); };
  const widen = (sec: number) => {
    if (!custom) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    const a = custom.from - sec, b = custom.to + sec;
    setFD(secToDate(a)); setFT(secToTime(a)); setTD(secToDate(b)); setTT(secToTime(b));
    setDraft(d => ({ ...d, kind: "custom", preset: undefined, file: undefined }));
  };

  const eff = draft.kind === "custom" ? custom : resolve(draft, catalog?.data_from, catalog?.data_to);
  const span = eff ? eff.to - eff.from + 1 : 0;
  const bad = !eff || span <= 0;

  const apply = () => {
    if (bad || !eff) return;
    tg()?.HapticFeedback?.impactOccurred("medium");
    if (draft.kind === "custom") onApply({ kind: "custom", from: secToIso(eff.from), to: secToIso(eff.to) });
    else onApply(draft);
  };

  const chip = (active: boolean, label: string, onClick: () => void) => (
    <button key={label} className={"turbine-chip" + (active ? " active" : "")} style={{ padding: "6px 11px", fontSize: 13 }} onClick={onClick}>
      <span className="turbine-chip-name">{label}</span>
    </button>
  );
  const quick = ["all", ...Object.keys(PRESET_HOURS), "lastday"];
  const sec = (k: string) => <div style={{ fontSize: 11, color: "var(--hint)", textTransform: "uppercase", letterSpacing: ".06em", margin: "12px 0 6px" }}>{t(k)}</div>;
  const inp: React.CSSProperties = { width: "100%", minWidth: 0, fontSize: 14, padding: "8px 8px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--bg2)", color: "var(--text)", fontVariantNumeric: "tabular-nums" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{t("period.title")}</h2>

        {sec("period.quick")}
        <div className="turbine-switcher" style={{ marginBottom: 0 }}>
          {quick.map(k => chip(draft.kind === (k === "all" ? "all" : "preset") && (k === "all" || draft.preset === k), t("period.q_" + k), () => pick(presetSel(k, catalog?.data_to))))}
        </div>

        {showFiles && files.length > 0 && (<>
          {sec("period.by_file")}
          <div className="turbine-switcher" style={{ marginBottom: 0 }}>
            {files.map(f => chip(draft.kind === "file" && draft.file?.id === f.id, `📄 ${f.filename.replace(/\.(csv|gz)$/i, "")}`,
              () => f.from_ts && f.to_ts && pick(fileSel({ id: f.id, filename: f.filename, unit: f.unit, from: f.from_ts, to: f.to_ts }))))}
          </div>
        </>)}

        {sec("period.custom")}
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr", gap: "6px 8px", alignItems: "center" }}>
          <label htmlFor="p-fd" style={{ fontSize: 13, color: "var(--hint)" }}>{t("period.from")}</label>
          <input id="p-fd" type="date" value={fD} onChange={e => editCustom(setFD)(e.target.value)} style={inp} />
          <input id="p-ft" type="time" step={1} value={fT} onChange={e => editCustom(setFT)(e.target.value)} style={inp} />
          <label htmlFor="p-td" style={{ fontSize: 13, color: "var(--hint)" }}>{t("period.to")}</label>
          <input id="p-td" type="date" value={tD} onChange={e => editCustom(setTD)(e.target.value)} style={inp} />
          <input id="p-tt" type="time" step={1} value={tT} onChange={e => editCustom(setTT)(e.target.value)} style={inp} />
        </div>
        <div className="turbine-switcher" style={{ marginTop: 8, marginBottom: 0 }}>
          {[[10, "± 10 " + t("period.s")], [60, "± 1 " + t("auto.min_short")], [600, "± 10 " + t("auto.min_short")], [3600, "± 1 " + t("period.h")]].map(([s, l]) =>
            chip(false, String(l), () => widen(Number(s))))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 10, fontSize: 13 }}>
          <span style={{ color: "var(--hint)" }}>{t("period.duration")}</span>
          <b style={{ fontVariantNumeric: "tabular-nums", color: bad ? "var(--destructive)" : undefined }}>
            {bad ? t("period.bad") : durationText(span, t) + (showStep ? ` · ${t("period.tables")} ${stepText(stepFor(span), t)}` : "")}
          </b>
        </div>
        {catalog?.data_from && catalog?.data_to && (
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 6 }}>
            {t("period.coverage", { s: label })}: {fmtDT(catalog.data_from)} → {fmtDT(catalog.data_to)}
          </div>
        )}
        {eff && !bad && (
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 2 }}>{fmtDT(secToIso(eff.from))} → {fmtDT(secToIso(eff.to))}</div>
        )}
        <button className="btn" style={{ marginTop: 12 }} disabled={bad} onClick={apply}>{t("period.apply")}</button>
        {value.kind !== "all" && (
          <button className="btn secondary" style={{ marginTop: 8 }} onClick={() => { onApply(ALL); }}>{t("period.q_all")}</button>
        )}
      </div>
    </div>
  );
}
